import { requestCodexText } from './codexText';
import { generateImage } from './imageGeneration';
import { loadCanvasImage, loadClothingSource } from './clothingEdit';
import { blendClothingPixels, growClothingMask, featherClothingMask, clothingMaskFeatherRadius } from './clothingMask';
import { changedFeatures, featureAnalysisPrompt, featureEditPrompt, parseFeatureAnalysis, editMaskToOpenAI, protectHeadPixels } from './characterFeatures';
import type { FeatureAnalysis, FeaturePolygon, FeatureValue } from './characterFeatureTypes';

export async function analyzeCharacterFeatures(imageUrl: string, stage: 'head' | 'body', description: string): Promise<FeatureAnalysis> {
    return parseFeatureAnalysis(await requestCodexText(featureAnalysisPrompt(stage, description), [imageUrl]), stage);
}
export function drawFeaturePolygons(ctx: CanvasRenderingContext2D, polygons: FeaturePolygon[], width: number, height: number, color = 'white') {
    ctx.fillStyle = ctx.strokeStyle = color;
    ctx.lineJoin = 'round'; ctx.lineWidth = Math.max(2, Math.min(width, height) / 300);
    for (const polygon of polygons) {
        ctx.beginPath();
        polygon.forEach(([x,y], index) => index ? ctx.lineTo(x*width/1000,y*height/1000) : ctx.moveTo(x*width/1000,y*height/1000));
        ctx.closePath();ctx.fill();ctx.stroke();
    }
}
const canvasBlob = (canvas: HTMLCanvasElement) => new Promise<Blob>((resolve,reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Could not save the edited image.')), 'image/png'));
export async function editCharacterFeatures(source: string, analysis: FeatureAnalysis, values: Record<string, FeatureValue>, stage: 'head' | 'body', lockedHeadUrl?: string): Promise<Blob> {
    const prompt = featureEditPrompt(analysis, values, stage);
    const image = await loadCanvasImage(await loadClothingSource(source));
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d', {willReadFrequently:true})!;
    ctx.drawImage(image,0,0);
    const original = ctx.getImageData(0,0,canvas.width,canvas.height);
    const sourcePng = canvas.toDataURL('image/png');
    const local = !changedFeatures(analysis, values).some(feature => feature.scope === 'holistic');
    ctx.fillStyle = 'black';ctx.fillRect(0,0,canvas.width,canvas.height);
    if (local) drawFeaturePolygons(ctx, changedFeatures(analysis, values).flatMap(feature => feature.regions), canvas.width, canvas.height);
    else {ctx.fillStyle='white';ctx.fillRect(0,0,canvas.width,canvas.height);}
    const selection = ctx.getImageData(0,0,canvas.width,canvas.height);
    if (local) {
        const delta=Math.max(0,...changedFeatures(analysis,values).map(feature=>typeof feature.value==='number'&&typeof values[feature.id]==='number'?Math.abs(Number(values[feature.id])-feature.value)/10:0));
        // Give local size/shape edits room to grow, then soften their seam.
        const margin=Math.round(Math.min(canvas.width,canvas.height)*(.008+.04*delta));
        selection.data.set(growClothingMask(selection.data,canvas.width,canvas.height,Math.max(3,margin)));
        selection.data.set(featherClothingMask(selection.data,canvas.width,canvas.height,clothingMaskFeatherRadius(canvas.width,canvas.height)));
    }
    ctx.fillStyle='black';ctx.fillRect(0,0,canvas.width,canvas.height);
    drawFeaturePolygons(ctx,analysis.protectedHead,canvas.width,canvas.height);
    const head = ctx.getImageData(0,0,canvas.width,canvas.height).data;
    if(stage==='body') {
        if (!analysis.protectedHead.length) throw new Error('Locate the locked head before editing.');
        for(let i=0;i<selection.data.length;i+=4) if(head[i]>0) selection.data[i]=selection.data[i+1]=selection.data[i+2]=0;
    }
    if(!selection.data.some((n,i)=>i%4===0&&n>0)) throw new Error('The selected regions are protected. Analyze controls again.');
    ctx.putImageData(new ImageData(editMaskToOpenAI(selection.data),canvas.width,canvas.height),0,0);
    const result = await generateImage({model:'openai/gpt-image-2.5-sunburst',prompt,aspectRatio:stage==='head'?'1:1':'2:3',resolution:'High',
        referenceImages:[sourcePng,...(lockedHeadUrl?[lockedHeadUrl]:[])],
        ...(local ? {maskImage:canvas.toDataURL('image/png'),editMode:'inpainting' as const} : {})});
    if(!result.imageBlob) throw new Error('ChatGPT returned no edited image.');
    const url=URL.createObjectURL(result.imageBlob);
    try {
        const edited=await loadCanvasImage(url);
        if(Math.abs(edited.naturalWidth/edited.naturalHeight-canvas.width/canvas.height)>0.02) throw new Error('The edit changed the canvas proportions. The original image is kept.');
        ctx.clearRect(0,0,canvas.width,canvas.height);ctx.drawImage(edited,0,0,canvas.width,canvas.height);
        const pixels=ctx.getImageData(0,0,canvas.width,canvas.height);
        if(local) pixels.data.set(blendClothingPixels(original.data,pixels.data,selection.data));
        if(stage==='body') pixels.data.set(protectHeadPixels(original.data,pixels.data,head));
        ctx.putImageData(pixels,0,0);
        return canvasBlob(canvas);
    } finally {URL.revokeObjectURL(url);}
}
